/**
 * The home-hero search console's state, as a pure reducer (HeroSearch.tsx renders it).
 *
 * The rules the owner asked for live here, so they are tested without rendering:
 *   - Defaults: للإيجار + سكني (the same defaults as /search).
 *   - Switching للإيجار / للبيع clears ONLY the price range: rents and sale prices are
 *     different magnitudes, every other filter still applies. So does switching the
 *     rent price between شهري and سنوي (default سنوي).
 *   - The last value of each «من / إلى» list is open-ended («5+»); a «إلى 5+» is
 *     dropped when the URL is built (config/searchPresets dropOpenEndedMaxes).
 *   - Switching سكني / تجاري drops a chosen type that is not in the new category and
 *     blanks the ranges that category hides (config/searchFilterFields — with no type,
 *     تجاري shows only price and area).
 *   - Choosing a type keeps the toggle in step and blanks the ranges that type hides
 *     (land: no bedrooms or bathrooms).
 *   - The «الحي أو صف ما تريد» field holds free text OR a picked place. Picking a
 *     district shows its name and sets its city; typing again turns it back into
 *     free text. A city suggestion moves into the city picker instead.
 *
 * Hidden values are also pruned once more when the URL is built
 * (utils/heroSearchParams → pruneHiddenFilters), so none can ever be sent.
 */
import type { LocationSuggestion } from '../../../services/neighborhoodMapService';
import { matchSaudiCity, type ResolvedSaudiCity } from '../../../config/saudiCities';
import { findSearchType } from '../../../config/propertyTypeCatalog';
import {
  createEmptySearchRanges,
  getVisibleRangeKeys,
  isInvertedRange,
  pruneHiddenFilters,
  rangesFromFilters,
  rangesToFilters,
  type SearchRangeBounds,
  type SearchRangeKey,
  type SearchRanges,
} from '../../../config/searchFilterFields';
import type { HeroSearchState } from '../../../utils/heroSearchParams';
import { DEFAULT_RENT_PRICE_PERIOD, type RentPricePeriod } from '../../../config/searchPresets';

export type HeroListing = 'rent' | 'buy';
export type HeroCategory = 'residential' | 'commercial';

export interface HeroFormState {
  listing: HeroListing;
  category: HeroCategory;
  /** The city picker's choice (null = «كل المدن»). */
  city: ResolvedSaudiCity | null;
  /** What the «الحي أو صف ما تريد» field shows. */
  text: string;
  /** A district (or a city the canonical list does not know) picked from the suggestions. */
  location: LocationSuggestion | null;
  /** UPPERCASE enum ('VILLA') or '' for every type. */
  propertyType: string;
  ranges: SearchRanges;
  /** The period rent prices are typed in (the شهري / سنوي pill). */
  pricePeriod: RentPricePeriod;
}

export type HeroFormAction =
  | { type: 'listing'; listing: HeroListing }
  | { type: 'category'; category: HeroCategory }
  | { type: 'city'; city: ResolvedSaudiCity | null }
  | { type: 'text'; text: string }
  /**
   * A suggestion was picked. `city` is the canonical city it belongs to (for a city
   * suggestion, the city itself), already carrying the suggestion's database id;
   * null when the canonical list has no match.
   */
  | { type: 'suggestion'; location: LocationSuggestion; label: string; city: ResolvedSaudiCity | null }
  | { type: 'propertyType'; propertyType: string }
  | { type: 'range'; key: SearchRangeKey; bounds: SearchRangeBounds }
  | { type: 'pricePeriod'; period: RentPricePeriod }
  | { type: 'clearRanges' };

export function createHeroFormState(): HeroFormState {
  return {
    listing: 'rent',
    category: 'residential',
    city: null,
    text: '',
    location: null,
    propertyType: '',
    ranges: createEmptySearchRanges(),
    pricePeriod: DEFAULT_RENT_PRICE_PERIOD,
  };
}

/** Blanks the ranges the type / category hides. Returns `ranges` itself when nothing changes. */
export function pruneRanges(ranges: SearchRanges, propertyType: string, category: HeroCategory): SearchRanges {
  const flat = { ...rangesToFilters(ranges), propertyType, category };
  const pruned = pruneHiddenFilters(flat);
  return pruned === flat ? ranges : rangesFromFilters(pruned);
}

/**
 * Does a picked place sit in `city`? Compared by canonical city (the database can
 * hold two rows for one city, e.g. «جدة» and "Jeddah"), then by database id.
 */
export function locationInCity(location: LocationSuggestion, city: ResolvedSaudiCity): boolean {
  // A city outside the canonical list is replaced by whatever the picker now says.
  if (location.kind === 'city') return false;
  const parent = matchSaudiCity(location.cityNameAr) ?? matchSaudiCity(location.cityNameEn);
  if (parent) return parent.slug === city.slug;
  return Boolean(city.cityId) && city.cityId === location.cityId;
}

/** A suggestion's name in the UI language (the other language when one is missing). */
export function suggestionLabel(location: LocationSuggestion, isArabic: boolean): string {
  return isArabic ? location.nameAr || location.nameEn : location.nameEn || location.nameAr;
}

export function heroFormReducer(state: HeroFormState, action: HeroFormAction): HeroFormState {
  switch (action.type) {
    case 'listing': {
      if (action.listing === state.listing) return state;
      const { price } = state.ranges;
      const ranges = price.min || price.max ? { ...state.ranges, price: { min: '', max: '' } } : state.ranges;
      return { ...state, listing: action.listing, ranges };
    }

    case 'category': {
      if (action.category === state.category) return state;
      const option = findSearchType(state.propertyType);
      const propertyType = option && option.searchCategory !== action.category ? '' : state.propertyType;
      return {
        ...state,
        category: action.category,
        propertyType,
        ranges: pruneRanges(state.ranges, propertyType, action.category),
      };
    }

    case 'city': {
      const next = action.city;
      const prev = state.city;
      // The same city again (e.g. its database id just arrived): keep the picked district.
      if (next && prev && next.slug === prev.slug) return next === prev ? state : { ...state, city: next };
      // «كل المدن» keeps a picked district (it still names one exact place).
      const keepLocation = !state.location || !next || locationInCity(state.location, next);
      return keepLocation ? { ...state, city: next } : { ...state, city: next, location: null, text: '' };
    }

    case 'text':
      if (action.text === state.text && !state.location) return state;
      // Typing over a picked district turns the field back into free text.
      return { ...state, text: action.text, location: null };

    case 'suggestion': {
      const { location, label, city } = action;
      if (location.kind === 'city' && city) {
        // A city belongs in the city picker; the text field empties for a district or a description.
        return { ...state, city, location: null, text: '' };
      }
      const nextCity = city && (!state.city || state.city.slug !== city.slug) ? city : state.city;
      return { ...state, city: nextCity, location, text: label };
    }

    case 'propertyType': {
      const option = findSearchType(action.propertyType);
      const propertyType = option ? option.enum : '';
      const category = option ? option.searchCategory : state.category;
      if (propertyType === state.propertyType && category === state.category) return state;
      return { ...state, propertyType, category, ranges: pruneRanges(state.ranges, propertyType, category) };
    }

    case 'range':
      return { ...state, ranges: { ...state.ranges, [action.key]: action.bounds } };

    case 'pricePeriod': {
      if (action.period === state.pricePeriod) return state;
      // 4,000 a month and 48,000 a year are different numbers: the typed price no longer fits.
      const { price } = state.ranges;
      const ranges = price.min || price.max ? { ...state.ranges, price: { min: '', max: '' } } : state.ranges;
      return { ...state, pricePeriod: action.period, ranges };
    }

    case 'clearRanges':
      return hasAnyRange(state.ranges) ? { ...state, ranges: createEmptySearchRanges() } : state;

    default:
      return state;
  }
}

function hasAnyRange(ranges: SearchRanges): boolean {
  return Object.values(ranges).some((bounds) => Boolean(bounds.min || bounds.max));
}

/** How many of the shown range filters have a value (the «الفلاتر» badge). */
export function activeRangeCount(state: HeroFormState): number {
  return getVisibleRangeKeys(state.propertyType, state.category).filter((key) => {
    const bounds = state.ranges[key];
    return Boolean(bounds.min || bounds.max);
  }).length;
}

/** The first shown range whose «من» is above its «إلى», or null. Searching waits until it is fixed. */
export function firstInvertedRange(state: HeroFormState): SearchRangeKey | null {
  return getVisibleRangeKeys(state.propertyType, state.category).find((key) => isInvertedRange(state.ranges[key])) ?? null;
}

/** The form state in the shape the /search deep-link builder takes. */
export function toHeroSearchInput(
  state: HeroFormState,
  options: { isRTL: boolean; view?: 'map' },
): HeroSearchState {
  return {
    tab: state.listing,
    category: state.category,
    location: state.location,
    city: state.city,
    // While the field shows a picked district, its text is a place, not a keyword.
    keyword: state.location ? '' : state.text,
    isRTL: options.isRTL,
    propertyType: state.propertyType,
    ...rangesToFilters(state.ranges),
    pricePeriod: state.pricePeriod,
    view: options.view,
  };
}
